import { Component, useEffect, useRef, ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SwipeableToaster from './components/SwipeableToaster';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ActivePlaybackProvider } from './context/ActivePlaybackContext';
import { setupForegroundListener } from './services/pushNotifications';

// Layouts
import DJLayout from './components/layouts/DJLayout';
import AttendeeLayout from './components/layouts/AttendeeLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import RequestInviteCodePage from './pages/auth/RequestInviteCodePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// DJ Pages
import DashboardPage from './pages/dj/DashboardPage';
import ArchivePage from './pages/dj/ArchivePage';
import CreateEventPage from './pages/dj/CreateEventPage';
import EventManagePage from './pages/dj/EventManagePage';
import SettingsPage from './pages/dj/SettingsPage';
import SongLibraryPage from './pages/dj/SongLibraryPage';
import MatchLibraryPage from './pages/dj/MatchLibraryPage';
import TestHarnessPage from './pages/dj/TestHarnessPage';

// Attendee Pages
import EventPage from './pages/attendee/EventPage';

// Public Pages
import RoadmapPage from './pages/public/RoadmapPage';
import AboutPage from './pages/public/AboutPage';
import { PrivacyPage } from './pages/public/PrivacyPage';
import { NextEventContactPage } from './pages/public/NextEventContactPage';
import { TermsPage } from './pages/public/TermsPage';
import NotFoundPage from './pages/public/NotFoundPage';
import PlaylistPage from './pages/public/PlaylistPage';
import DanceOffClaimPage from './pages/attendee/DanceOffClaimPage';
import PlatformsPage from './pages/public/PlatformsPage';

// Preview Device
import PreviewPage from './pages/dj/PreviewPage';

// VIP Trigger
import VipTriggerPage from './pages/public/VipTriggerPage';

// Co-Approval
import CoApprovalPage from './pages/public/CoApprovalPage';
import CoApprovalDjPage from './pages/public/CoApprovalDjPage';

// Invite Request Email Actions
import { InviteRequestActionPage } from './pages/public/InviteRequestActionPage';

// Visualizer Projection Window
import ProjectionPage from './visualizer/ProjectionPage';

// Owner Admin
import { AdminPage } from './pages/admin/AdminPage';

// Route Guards
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';

// Error Boundary — prevents white screen on uncaught errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '2rem', textAlign: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
            <p style={{ color: '#666', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred.'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1.5rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const recentNotificationsRef = useRef<Set<string>>(new Set());

  // Set up foreground FCM listener — shows toasts when the tab is focused
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    setupForegroundListener((title, body, _url, type) => {
      if (type === 'rally-out' || type === 'rally-back') return;
      const key = `${title}|${body}`;
      if (recentNotificationsRef.current.has(key)) return;
      recentNotificationsRef.current.add(key);
      setTimeout(() => recentNotificationsRef.current.delete(key), 10000);
      toast(
        `${title}\n${body}`,
        { icon: '🔔', duration: 5000 }
      );
    }).then((unsub) => { unsubscribe = unsub; });
    return () => { unsubscribe?.(); };
  }, []);

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <ActivePlaybackProvider>
        <SwipeableToaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1E293B',
              color: '#F8FAFC',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#F8FAFC',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#F8FAFC',
              },
            },
          }}
        />

        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/request-code" element={<RequestInviteCodePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Protected DJ Routes */}
          <Route element={<ProtectedRoute><DJLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/events/create" element={<CreateEventPage />} />
            <Route path="/events/:eventId/manage" element={<EventManagePage />}>
              <Route index element={<Navigate to="queue" replace />} />
              <Route path="queue" element={null} />
              <Route path="pending" element={null} />
              <Route path="messages" element={null} />
              <Route path="analytics" element={null} />
              <Route path="feedback" element={null} />
              <Route path="settings" element={null} />
              <Route path="prep" element={null} />
            </Route>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/song-library" element={<SongLibraryPage />} />
            <Route path="/match-library" element={<MatchLibraryPage />} />
            <Route path="/test-harness" element={<TestHarnessPage />} />
          </Route>

          {/* Owner Admin (outside DJLayout so the DJ sidebar never links to it) */}
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />

          {/* Public Attendee Routes */}
          <Route element={<AttendeeLayout />}>
            <Route path="/e/:eventId" element={<EventPage />} />
            <Route path="/e/:eventId/playlist" element={<PlaylistPage />} />
            <Route path="/e/:eventId/claim/:claimId" element={<DanceOffClaimPage />} />
          </Route>

          {/* Preview Device Route (token-authenticated, no layout wrapper) */}
          <Route path="/events/:eventId/preview" element={<PreviewPage />} />

          {/* VIP Trigger Route (token-authenticated, no layout wrapper) */}
          <Route path="/events/:eventId/vip" element={<VipTriggerPage />} />

          {/* Co-Approval Route (token-authenticated, no layout wrapper) */}
          <Route path="/events/:eventId/co-approval" element={<CoApprovalPage />} />

          {/* Co-Approval DJ Route (token-authenticated, no layout wrapper) (ITER-194) */}
          <Route path="/events/:eventId/co-approval-dj" element={<CoApprovalDjPage />} />

          {/* Invite Request Email Action Route (token-authenticated, no layout wrapper) */}
          <Route path="/invite-action" element={<InviteRequestActionPage />} />

          {/* Visualizer Projection Window (pop-out, no layout) */}
          <Route path="/visualizer/projection" element={<ProjectionPage />} />

          {/* Public Routes */}
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/next-event" element={<NextEventContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/platforms" element={<PlatformsPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ActivePlaybackProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
